import { Navigate, Outlet, useLocation } from "react-router";

import { useAppSelector } from "@/app/hooks";
import { RouteLoadingFallback } from "@/app/routes/RouteLoadingFallback";
import {
	selectAuthSessionStatus,
	selectOnboardingServerStatus,
} from "@/features/auth/authSessionSlice";

function RouteAccessBoundary({
	mode,
}: {
	mode: "authenticated" | "onboarding" | "public-auth" | "root-entry";
}) {
	const sessionStatus = useAppSelector(selectAuthSessionStatus);
	const onboardingStatus = useAppSelector(selectOnboardingServerStatus);
	const location = useLocation();

	if (mode === "authenticated" && location.pathname === "/foundation") {
		return <Outlet />;
	}

	if (sessionStatus === "booting") {
		return <RouteLoadingFallback />;
	}
	if (mode === "root-entry") {
		if (sessionStatus === "authenticated") {
			return (
				<Navigate
					replace
					to={
						onboardingStatus === "completed"
							? "/workspace"
							: "/onboarding/welcome"
					}
				/>
			);
		}
		return <Outlet />;
	}
	if (mode === "public-auth") {
		if (sessionStatus === "authenticated") {
			return (
				<Navigate
					replace
					to={
						onboardingStatus === "completed"
							? "/workspace"
							: "/onboarding/welcome"
					}
				/>
			);
		}
		return <Outlet />;
	}
	if (sessionStatus !== "authenticated") {
		return <Navigate replace to="/login" />;
	}
	if (mode === "onboarding") {
		return onboardingStatus === "completed" ? (
			<Navigate replace to="/workspace" />
		) : (
			<Outlet />
		);
	}
	return onboardingStatus === "completed" ? (
		<Outlet />
	) : (
		<Navigate replace to="/onboarding/welcome" />
	);
}

export function AuthenticatedRouteBoundary() {
	return <RouteAccessBoundary mode="authenticated" />;
}

export function OnboardingRouteBoundary() {
	return <RouteAccessBoundary mode="onboarding" />;
}

export function PublicAuthRouteBoundary() {
	return <RouteAccessBoundary mode="public-auth" />;
}

export function RootEntryRouteBoundary() {
	return <RouteAccessBoundary mode="root-entry" />;
}
